/**
 * Projects API
 *
 * API functions for project CRUD operations.
 */

import { api } from "./client";
import type {
  Project,
  ProjectId,
  ProjectListResponse,
  ProjectMember,
  ProjectMemberListResponse,
  ProjectContext,
  ProjectQuotaStatus,
  CreateProjectRequest,
  UpdateProjectRequest,
  UpdateProjectContextRequest,
  AddMemberRequest,
  UserId,
} from "./types";

export async function listProjects(): Promise<ProjectListResponse> {
  return api.get<ProjectListResponse>("/projects");
}

export async function getProject(id: ProjectId): Promise<Project> {
  return api.get<Project>(`/projects/${id}`);
}

export async function createProject(
  req: CreateProjectRequest,
): Promise<Project> {
  return api.post<Project>("/projects", req);
}

export async function updateProject(
  id: ProjectId,
  req: UpdateProjectRequest,
): Promise<Project> {
  return api.patch<Project>(`/projects/${id}`, req);
}

export async function deleteProject(id: ProjectId): Promise<void> {
  return api.delete(`/projects/${id}`);
}

export async function archiveProject(id: ProjectId): Promise<Project> {
  return api.post<Project>(`/projects/${id}/archive`, {});
}

/**
 * Rejects with a 409 `ApiError` carrying `{ error, code: "PROJECT_CAP_REACHED" }`
 * when the tenant is already at its plan's active project cap (FLOW-889).
 */
export async function unarchiveProject(id: ProjectId): Promise<Project> {
  return api.post<Project>(`/projects/${id}/unarchive`, {});
}

export async function listMembers(
  projectId: ProjectId,
): Promise<ProjectMemberListResponse> {
  return api.get<ProjectMemberListResponse>(`/projects/${projectId}/members`);
}

export async function addMember(
  projectId: ProjectId,
  req: AddMemberRequest,
): Promise<ProjectMember> {
  return api.post<ProjectMember>(`/projects/${projectId}/members`, req);
}

export async function removeMember(
  projectId: ProjectId,
  userId: UserId,
): Promise<void> {
  return api.delete(`/projects/${projectId}/members/${userId}`);
}

/** `usage` is omitted unless the caller is an org admin or the owner (FLOW-850). */
export async function getProjectQuota(
  id: ProjectId,
): Promise<ProjectQuotaStatus> {
  return api.get<ProjectQuotaStatus>(
    `/projects/${encodeURIComponent(id)}/quota`,
  );
}

export async function getProjectContext(
  projectId: ProjectId,
): Promise<ProjectContext> {
  return api.get<ProjectContext>(`/projects/${projectId}/context`);
}

export async function updateProjectContext(
  projectId: ProjectId,
  req: UpdateProjectContextRequest,
): Promise<ProjectContext> {
  return api.put<ProjectContext>(`/projects/${projectId}/context`, req);
}
